import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Send, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { trackEvent } from "@/components/Analytics";
import { homeContent } from "@/data/content";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const buildWizardSummary = (wizard, answers, formData) => {
  const lookupLabel = (stepId, value) => {
    const step = wizard.steps.find((s) => s.id === stepId);
    if (!step?.options) return value;
    const opt = step.options.find((o) => o.value === value);
    return opt ? opt.label : value;
  };

  const lines = ["Nouveau lead via le wizard :", ""];
  lines.push(`• Type : ${lookupLabel("type", answers.type)}`);
  lines.push(`• Taille : ${lookupLabel("size", answers.size)}`);
  lines.push(`• Secteur : ${lookupLabel("sector", answers.sector)}`);
  const features = (answers.features || []).map((v) => lookupLabel("features", v));
  lines.push(`• Fonctionnalités : ${features.length ? features.join(", ") : "—"}`);
  lines.push(`• Délai : ${lookupLabel("timeline", answers.timeline)}`);
  lines.push("");
  lines.push(`Contact : ${formData.name || "—"}${formData.company ? ` (${formData.company})` : ""}`);
  lines.push(`Email : ${formData.email}`);
  if (formData.note) {
    lines.push("");
    lines.push("Note :");
    lines.push("---");
    lines.push(formData.note);
    lines.push("---");
  }
  return lines.join("\n");
};

const WizardProgress = ({ current, total, label, ofLabel }) => {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
        <span>{label} {current} {ofLabel} {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const RadioStep = ({ step, value, onChange }) => (
  <div className="space-y-2.5">
    {step.options.map((option) => {
      const active = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={active}
          className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
            active
              ? "border-primary bg-primary/5"
              : "border-border bg-card hover:border-primary/40"
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                active ? "border-primary bg-primary" : "border-border"
              }`}
            >
              {active && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground leading-tight">{option.label}</p>
              {option.sub && (
                <p className="text-sm text-muted-foreground mt-0.5">{option.sub}</p>
              )}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

const MultiStep = ({ step, values, onChange }) => {
  const toggle = (option) => {
    const current = values || [];
    if (option.exclusive) {
      onChange(current.includes(option.value) ? [] : [option.value]);
      return;
    }
    const next = current.filter((v) => {
      const opt = step.options.find((o) => o.value === v);
      return !opt?.exclusive;
    });
    if (next.includes(option.value)) {
      onChange(next.filter((v) => v !== option.value));
    } else {
      onChange([...next, option.value]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2.5">
      {step.options.map((option) => {
        const active = (values || []).includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggle(option)}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border-2 transition-all ${
              active
                ? "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20"
                : "bg-card text-foreground border-border hover:border-primary/40"
            }`}
          >
            {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const ContactStep = ({ formData, onChange, form }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-semibold mb-1.5">
        {form.emailLabel} <span className="text-primary">{form.emailRequired}</span>
      </label>
      <Input
        name="email"
        type="email"
        value={formData.email}
        onChange={onChange}
        placeholder={form.emailPlaceholder}
        required
        autoFocus
        className="input-playful"
      />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-sm font-semibold mb-1.5">
          {form.nameLabel} <span className="text-xs text-muted-foreground font-normal">({form.nameSub})</span>
        </label>
        <Input
          name="name"
          value={formData.name}
          onChange={onChange}
          placeholder={form.namePlaceholder}
          className="input-playful"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-1.5">
          {form.companyLabel} <span className="text-xs text-muted-foreground font-normal">({form.companySub})</span>
        </label>
        <Input
          name="company"
          value={formData.company}
          onChange={onChange}
          placeholder={form.companyPlaceholder}
          className="input-playful"
        />
      </div>
    </div>
    <div>
      <label className="block text-sm font-semibold mb-1.5">
        {form.noteLabel} <span className="text-xs text-muted-foreground font-normal">({form.noteSub})</span>
      </label>
      <Textarea
        name="note"
        value={formData.note}
        onChange={onChange}
        placeholder={form.notePlaceholder}
        rows={4}
        className="input-playful resize-y min-h-[100px]"
      />
    </div>
    <p className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed pt-1">
      <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" strokeWidth={2.5} />
      <span>{form.privacy}</span>
    </p>
  </div>
);

const FreeformPanel = ({ c, onSwitchToWizard, onSuccess, source, subjectPrefix }) => {
  const f = c.freeform;
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const prefix = subjectPrefix ? `${subjectPrefix} ` : "";
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `${prefix}Nouveau contact Lacrea: ${formData.name}`,
          from_name: `Lacrea.fr (${source})`,
          source: `${source}-freeform`,
          name: formData.name,
          email: formData.email,
          company: formData.company || "—",
          message: formData.message,
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message);
      trackEvent("generate_lead", { source: `${source}-freeform` });
      onSuccess();
    } catch (error) {
      toast.error(c.errors.title, { description: c.errors.description });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold mb-1.5">
            {f.nameLabel} <span className="text-primary">{f.nameRequired}</span>
          </label>
          <Input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={f.namePlaceholder}
            required
            className="input-playful"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5">
            {f.emailLabel} <span className="text-primary">{f.emailRequired}</span>
          </label>
          <Input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={f.emailPlaceholder}
            required
            className="input-playful"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold mb-1.5">
          {f.companyLabel} <span className="text-xs text-muted-foreground font-normal">({f.companySub})</span>
        </label>
        <Input
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder={f.companyPlaceholder}
          className="input-playful"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold mb-1.5">
          {f.messageLabel} <span className="text-primary">{f.messageRequired}</span>
        </label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder={f.messagePlaceholder}
          rows={6}
          required
          className="input-playful resize-y min-h-[140px]"
        />
      </div>
      <p className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed pt-1">
        <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <span>{c.form.privacy}</span>
      </p>
      <div className="flex items-center justify-between gap-3 pt-1">
        <Button
          type="button"
          onClick={onSwitchToWizard}
          className="rounded-full h-11 px-5 text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {c.switchToWizard}
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="rounded-full h-11 px-6 text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {isSubmitting ? (
            <>
              <span className="spinner mr-2" />
              {f.submitLoadingLabel}
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              {f.submitLabel}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

const ProjectWizard = ({ source = "home-wizard", subjectPrefix }) => {
  const c = homeContent.projectWizard;
  const [mode, setMode] = useState("wizard");
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [formData, setFormData] = useState({ email: "", name: "", company: "", note: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const tracking = useRef({});

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const pick = (k) => p.get(k) || "";
      tracking.current = {
        utm_source: pick("utm_source"),
        utm_medium: pick("utm_medium"),
        utm_campaign: pick("utm_campaign"),
        utm_content: pick("utm_content"),
        utm_term: pick("utm_term"),
        gclid: pick("gclid"),
        referrer: document.referrer || "",
        landing_page: window.location.pathname + window.location.search,
      };
    } catch (_) {}
  }, []);

  const totalSteps = c.steps.length;
  const step = c.steps[stepIdx];
  const isFinalStep = step.kind === "contact";

  const setAnswer = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }));

  const canProceed = () => {
    if (step.kind === "contact") return formData.email.trim().length > 3;
    if (!step.required) return true;
    const v = answers[step.id];
    if (step.kind === "multi") return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== null && v !== "";
  };

  const handleNext = () => {
    trackEvent("wizard_step_completed", { step_id: step.id, step_idx: stepIdx, source });
    if (stepIdx < totalSteps - 1) setStepIdx(stepIdx + 1);
  };

  const handleBack = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    setIsSubmitting(true);
    try {
      const summary = buildWizardSummary(c, answers, formData);
      const t = tracking.current || {};
      const prefix = subjectPrefix ? `${subjectPrefix} ` : "";
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `${prefix}[Wizard] ${answers.type || "?"} · ${answers.sector || "?"} — ${formData.email}`,
          from_name: `Lacrea.fr (${source})`,
          source,
          email: formData.email,
          name: formData.name || "—",
          company: formData.company || "—",
          note: formData.note || "—",
          project_type: answers.type || "—",
          project_size: answers.size || "—",
          project_sector: answers.sector || "—",
          project_features: (answers.features || []).join(", ") || "—",
          project_timeline: answers.timeline || "—",
          utm_source: t.utm_source || "—",
          utm_medium: t.utm_medium || "—",
          utm_campaign: t.utm_campaign || "—",
          utm_content: t.utm_content || "—",
          utm_term: t.utm_term || "—",
          gclid: t.gclid || "—",
          referrer: t.referrer || "—",
          landing_page: t.landing_page || "—",
          message: summary,
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message);
      trackEvent("generate_lead", {
        source,
        project_type: answers.type,
        project_sector: answers.sector,
      });
      setIsSubmitted(true);
    } catch (error) {
      toast.error(c.errors.title, { description: c.errors.description });
    } finally {
      setIsSubmitting(false);
    }
  };

  const restart = () => {
    setStepIdx(0);
    setAnswers({});
    setFormData({ email: "", name: "", company: "", note: "" });
    setIsSubmitted(false);
  };

  return (
    <div className="bg-card rounded-2xl p-6 sm:p-8 border border-border">
      {!isSubmitted && mode === "freeform" && (
        <FreeformPanel
          c={c}
          source={source}
          subjectPrefix={subjectPrefix}
          onSwitchToWizard={() => setMode("wizard")}
          onSuccess={() => setIsSubmitted(true)}
        />
      )}

      {!isSubmitted && mode === "wizard" && (
        <>
          <WizardProgress
            current={stepIdx + 1}
            total={totalSteps}
            label={c.stepLabel}
            ofLabel={c.stepOf}
          />

          <div className="flex justify-center mb-6">
            <Button
              type="button"
              onClick={() => setMode("freeform")}
              className="rounded-full h-11 px-6 text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {c.switchToFreeform}
            </Button>
          </div>

          <div className="mb-6">
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-1.5">
              {step.label}
            </h3>
            {step.sub && (
              <p className="text-sm text-muted-foreground">{step.sub}</p>
            )}
          </div>

          <div className="mb-8">
            {step.kind === "radio" && (
              <RadioStep
                step={step}
                value={answers[step.id]}
                onChange={(v) => setAnswer(step.id, v)}
              />
            )}
            {step.kind === "multi" && (
              <MultiStep
                step={step}
                values={answers[step.id]}
                onChange={(v) => setAnswer(step.id, v)}
              />
            )}
            {step.kind === "contact" && (
              <ContactStep
                formData={formData}
                onChange={handleFormChange}
                form={c.form}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={handleBack}
                disabled={stepIdx === 0}
                className="rounded-full h-11 px-5 text-sm font-semibold bg-transparent border border-border hover:bg-muted text-foreground disabled:opacity-0 disabled:pointer-events-none"
              >
                <ArrowLeft className="mr-1.5 w-4 h-4" />
                {c.backLabel}
              </Button>
              <Button
                type="button"
                onClick={restart}
                disabled={stepIdx === 0}
                className="rounded-full h-11 px-5 text-sm font-semibold bg-transparent border border-border hover:bg-muted text-foreground disabled:opacity-0 disabled:pointer-events-none"
              >
                {c.restartLabel}
              </Button>
            </div>
            {isFinalStep ? (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                className="rounded-full h-11 px-6 text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner mr-2" />
                    {c.form.submitLoadingLabel}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {c.form.submitLabel}
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleNext}
                disabled={!canProceed()}
                className="rounded-full h-11 px-6 text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {c.nextLabel}
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </Button>
            )}
          </div>
        </>
      )}

      {isSubmitted && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-5">
            <Check className="w-8 h-8 text-primary" strokeWidth={2.5} />
          </div>
          <h3 className="text-2xl font-bold mb-3">{c.success.title}</h3>
          <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-6">
            {c.success.body}
          </p>
          <Button
            type="button"
            onClick={restart}
            className="rounded-full h-11 px-6 text-sm font-semibold bg-transparent border border-border hover:bg-muted text-foreground"
          >
            {c.success.restartLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProjectWizard;
